import type { IsoDateStr } from '@moment/domain-types'
import type { CashflowTarget as ProposalCashflowTarget } from '@moment/horizon-data-access/proposal'
import type { CashflowTarget } from '@/types/api/accounts'

import { MaturityOption } from '@moment/horizon-data-access/proposal'
import { parseIsoDate } from '@moment/utils/datetime'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { min } from 'date-fns/min'
import { parseISO } from 'date-fns/parseISO'
import { Temporal } from 'temporal-polyfill'

import { type Cashflow, CashflowType } from '@/types/statistics'
import { DateFormat } from '@/utils/dates/dateFormatting'
import { formatMoney, pluralize } from '@/utils/presentation/formatting'

import { MANUAL_CASHFLOW_SOURCE } from './constants'
import { labelByYear, withGapMonths } from './gap-months'
import { parseStoredGranularity, windowMonths } from './granularity'
import {
  type PlannedBucket,
  planCashflowBuckets,
} from './merge-recurring-targets'
import { rowDatesInRange } from './target-bounds'
import {
  type CashflowBucket,
  type CashflowFormState,
  CashflowFrequency,
  CashflowFrequencyMap,
  CashflowSource,
  type CashflowTargetRowData,
} from './types'

/**
 * Creates cashflow buckets based on the provided form state
 */
export function createCashflowBucket(
  formState: CashflowFormState,
): CashflowBucket[] {
  const { target, cashflowFrequency, granularity, startDate, endDate } =
    formState

  // Dates past the backend's range would build a bar per period to year 9999.
  if (!rowDatesInRange(formState)) return []

  // A blank target still plans the schedule, as $0 bars.
  const amount = parseFloat(target) || 0
  const end = parseISO(endDate)

  if (cashflowFrequency === CashflowFrequency.OneTime) {
    const start_date = startDate
      ? format(parseISO(startDate), DateFormat.ISO_DATE)
      : null
    const displayDate = startDate ? parseISO(startDate) : end

    return [
      {
        start_date,
        end_date: format(end, DateFormat.ISO_DATE),
        amount,
        start_date_formatted: format(
          displayDate,
          DateFormat.MONTH_ABBREV_YEAR_ABBREV,
        ),
        coupon_amount: 0,
        principal_amount: 0,
        dividend_amount: 0,
        total_amount: 0,
      },
    ]
  }

  if (startDate === null) {
    return []
  }

  const start = parseISO(startDate)
  const buckets: CashflowBucket[] = []
  const monthInterval = CashflowFrequencyMap[cashflowFrequency]
  // Clamp defensively: a too-wide stored window must never make buckets overlap.
  const windowLength = Math.min(
    windowMonths(cashflowFrequency, granularity),
    monthInterval,
  )
  let currentStart = start
  let currentYearNoted = false
  let currentYear = currentStart.getFullYear()

  while (currentStart < end) {
    // Anchored on the original start like the backend, so month-end starts neither drift nor leave holes.
    const currentEnd = min([
      addMonths(start, buckets.length * monthInterval + windowLength),
      end,
    ])
    let formattedStart = ''

    if (currentStart.getFullYear() !== currentYear) {
      currentYear = currentStart.getFullYear()
      currentYearNoted = false
    }
    // Format the start date as the month abbreviation and year
    if (!currentYearNoted) {
      currentYearNoted = true
      formattedStart = format(currentStart, DateFormat.MONTH_ABBREV_YEAR_ABBREV)
    } else {
      formattedStart = format(currentStart, DateFormat.MONTH_ABBREV)
    }
    buckets.push({
      start_date: format(currentStart, DateFormat.ISO_DATE),
      end_date: format(currentEnd, DateFormat.ISO_DATE),
      amount,
      start_date_formatted: formattedStart,
      coupon_amount: 0,
      principal_amount: 0,
      dividend_amount: 0,
      total_amount: 0,
    })

    currentStart = addMonths(start, buckets.length * monthInterval)
  }

  return buckets
}

/** A target amount of 0 means the same as null/undefined: no fixed amount. */
export function isEmptyCashflowTarget(amount?: number | null): boolean {
  return amount == null || amount === 0
}

/**
 * Placeholder buckets for maximize mode: overridden buckets keep their fixed
 * amount; the rest get an even split of starting cash, flagged as projections.
 */
export function buildMaximizePlaceholderBuckets(
  formState: CashflowFormState,
  startingCash: number,
  overrides: Record<string, number> = {},
): CashflowBucket[] {
  const schedule = createCashflowBucket(formState)
  if (schedule.length === 0) return []

  const perBucket = Math.max(0, startingCash) / schedule.length
  return schedule.map((bucket) => {
    const override = lookupOverride(bucket, overrides)
    if (isEmptyCashflowTarget(override)) {
      return { ...bucket, amount: perBucket, isProjection: true }
    }
    return { ...bucket, amount: override ?? perBucket, isProjection: false }
  })
}

/**
 * Generates the override key for a bucket: its start date as "YYYYMMDD".
 * Null for a bucket with no start date: createCashflowBucket emits no recurring
 * buckets without one, so only one-time buckets reach this with a null start.
 */
export function generateOverrideKey(bucket: CashflowBucket): string | null {
  if (bucket.start_date === null) {
    return null
  }
  return format(parseISO(bucket.start_date), DateFormat.YEAR_MONTH_DAY)
}

function lookupOverride(
  bucket: CashflowBucket,
  overrides: Record<string, number>,
): number | undefined {
  const key = generateOverrideKey(bucket)
  return key === null ? undefined : overrides[key]
}

/**
 * Rewrites legacy "YYYYMMDD_YYYYMMDD" keys to the bucket's start date, so
 * overrides saved before the key change still resolve. Unparseable keys are
 * dropped — the backend ignores them too.
 */
export function normalizeOverrideKeys(
  overrides: Record<string, number> | null | undefined,
): Record<string, number> {
  const normalized: Record<string, number> = {}
  const stored = overrides ?? {}
  for (const [key, amount] of Object.entries(stored)) {
    const startKey = key.split('_')[0]
    if (!startKey || !/^\d{8}$/.test(startKey)) continue
    // An exact start-date key wins, since it is the one the backend reads.
    if (key !== startKey && stored[startKey] !== undefined) continue
    normalized[startKey] = amount
  }
  return normalized
}

/**
 * Applies overrides to the generated bucket schedule. An override that matches
 * no generated bucket is dropped, mirroring the backend.
 */
export function mergeBucketsWithOverrides(
  baseBuckets: CashflowBucket[],
  overrides: Record<string, number> = {},
): CashflowBucket[] {
  return baseBuckets.map((bucket) => ({
    ...bucket,
    amount: lookupOverride(bucket, overrides) ?? bucket.amount,
  }))
}

// Total number of cashflow occurrences between the target's start and end dates
function numberOfPeriods(target: CashflowTarget): number {
  if (target.frequency === CashflowFrequency.OneTime) return 1
  // start_date is optional, so an absent one is undefined, not null.
  if (!target.start_date) return 0

  const monthInterval = CashflowFrequencyMap[target.frequency]
  const start = parseISO(target.start_date)
  const end = parseISO(target.end_date)

  let count = 0
  while (addMonths(start, count * monthInterval) < end) {
    count++
  }
  return count
}

/**
 * Normalizes cashflow targets into a single total. Makes conservative assumptions
 * (2% annualized coupon for all bonds) for amount needed to safely meet the targets.
 */
export function sumNormalizedCashflowTargets(
  targets: CashflowTarget[],
): number {
  let summedNormalizedTargets = 0
  for (const target of targets) {
    const amount = target.amount ?? 0
    if (target.source === CashflowSource.Coupon) {
      // conservative assumption of 2% annualized coupon for ALL bonds
      summedNormalizedTargets += amount * 50 * numberOfPeriods(target)
    } else {
      summedNormalizedTargets += amount * numberOfPeriods(target)
    }
  }
  return summedNormalizedTargets
}

export function getCashflowFrequencyOptions() {
  return Object.values(CashflowFrequency).map((frequency) => {
    if (frequency === CashflowFrequency.OneTime) {
      return {
        label: 'One Time',
        value: frequency,
      }
    }
    const monthCount =
      CashflowFrequencyMap[frequency as keyof typeof CashflowFrequencyMap]
    return {
      label: `${monthCount} ${pluralize(monthCount, 'month', 'months')}`,
      value: frequency,
    }
  })
}

export function getCashflowSourceOptions() {
  return Object.values(CashflowSource).map((source) => {
    return {
      label: source
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()),
      value: source,
    }
  })
}
/**
 * Finds the appropriate bucket for a cashflow date based on date range
 */
function findBucketForCashflowDate(
  date: string,
  buckets: CashflowBucket[],
  scheduleEndDate: string,
): CashflowBucket | undefined {
  const cashflowDate = parseISO(date)

  return buckets.find((bucket, index) => {
    const bucketEnd = parseISO(bucket.end_date)
    // Only the array-last bucket ending on the schedule end is closed; every other stays [start, end) like the backend.
    // That keeps a trimmed trailing window, or an earlier custom row sharing the end date, open.
    const isScheduleEnd =
      index === buckets.length - 1 && bucket.end_date === scheduleEndDate
    const endComparison = isScheduleEnd
      ? cashflowDate <= bucketEnd
      : cashflowDate < bucketEnd

    if (bucket.start_date === null) {
      return endComparison
    }

    const bucketStart = parseISO(bucket.start_date)
    return cashflowDate >= bucketStart && endComparison
  })
}

/**
 * Aggregates cashflow data into buckets by type (coupon/principal/dividend).
 * A call payment counts as principal. Mutates the buckets in place.
 */
function aggregateCashflowBuckets(
  cashflowData: Cashflow[],
  buckets: CashflowBucket[],
  scheduleEndDate: string,
): void {
  for (const cashflow of cashflowData) {
    const bucket = findBucketForCashflowDate(
      cashflow.date,
      buckets,
      scheduleEndDate,
    )
    if (!bucket) continue

    if (cashflow.type === CashflowType.Coupon) {
      bucket.coupon_amount += cashflow.amount
      bucket.total_amount += cashflow.amount
    } else if (
      cashflow.type === CashflowType.Principal ||
      cashflow.type === CashflowType.Call
    ) {
      bucket.principal_amount += cashflow.amount
      bucket.total_amount += cashflow.amount
    } else if (cashflow.type === CashflowType.Dividend) {
      bucket.dividend_amount += cashflow.amount
    }
  }
}

export function formatCashflowData(
  cashflowData: Cashflow[],
  cashflowTarget: CashflowTarget,
): CashflowBucket[] {
  // 1. Create baseline buckets from target
  const schedule = {
    target: String(cashflowTarget.amount ?? 0),
    cashflowFrequency: cashflowTarget.frequency,
    granularity: parseStoredGranularity(cashflowTarget),
    // Proposal targets omit an open start, so it can arrive undefined despite the type.
    startDate: cashflowTarget.start_date ?? null,
    endDate: cashflowTarget.end_date,
    source: cashflowTarget.source,
  }
  const buckets = createCashflowBucket(schedule)

  // 2. Apply overrides to target amounts
  const currentOverrides = normalizeOverrideKeys(cashflowTarget.overrides)
  for (const bucket of buckets) {
    const override = lookupOverride(bucket, currentOverrides)
    if (override === undefined) continue
    bucket.amount = override
  }

  // 3. Aggregate cashflows into windows and gap months alike. A gap slot has
  // no target, so its cash stays visible without counting toward any window.
  const slots = withGapMonths(buckets, schedule)
  aggregateCashflowBuckets(cashflowData, slots, cashflowTarget.end_date)
  return slots
}

const PLANNED_SOURCE: Record<PlannedBucket['source'], CashflowSource> = {
  coupon: CashflowSource.Coupon,
  principal: CashflowSource.Principal,
  coupon_and_principal: CashflowSource.CouponAndPrincipal,
}

function emptySlot(start: Temporal.PlainDate, end: Temporal.PlainDate) {
  return {
    start_date: start.toString(),
    end_date: end.toString(),
    amount: 0,
    start_date_formatted: '',
    coupon_amount: 0,
    principal_amount: 0,
    dividend_amount: 0,
    total_amount: 0,
  }
}

// Stepped from `from` itself so month-end dates don't drift.
function gapMonths(
  from: Temporal.PlainDate,
  until: Temporal.PlainDate,
): CashflowBucket[] {
  const gaps: CashflowBucket[] = []
  for (let k = 0; ; k++) {
    const gapStart = from.add({ months: k })
    if (Temporal.PlainDate.compare(gapStart, until) >= 0) return gaps
    const next = from.add({ months: k + 1 })
    const gapEnd = Temporal.PlainDate.compare(next, until) < 0 ? next : until
    gaps.push({ ...emptySlot(gapStart, gapEnd), isGap: true })
  }
}

interface PlannedSlotsOptions {
  buckets: PlannedBucket[]
  // The row whose bars are editable; null leaves every bar unmarked.
  editableIndex: number | null
}

/** One slot per planned bucket, with empty months only where no bucket reaches. */
export function plannedSlots({
  buckets,
  editableIndex,
}: PlannedSlotsOptions): CashflowBucket[] {
  const slots: CashflowBucket[] = []
  let reach: Temporal.PlainDate | null = null
  for (const bucket of buckets) {
    const start = parseIsoDate(bucket.start)
    const end = parseIsoDate(bucket.end)
    if (reach !== null) slots.push(...gapMonths(reach, start))
    const amount = bucket.amount ?? 0
    const source = PLANNED_SOURCE[bucket.source]
    const slot: CashflowBucket = { ...emptySlot(start, end), amount, source }
    if (editableIndex !== null) {
      const isLocked = !bucket.contributors.includes(editableIndex)
      slot.isLocked = isLocked
      const [owner] = bucket.contributors
      if (isLocked && owner !== undefined) slot.lockedByRow = owner
    }
    slots.push(slot)
    if (reach === null || Temporal.PlainDate.compare(end, reach) > 0) {
      reach = end
    }
  }
  return labelByYear(slots)
}

/** Planned slots with cash counted into buckets and gap months alike, so cash outside every window stays visible. */
export function formatPlannedData(
  cashflowData: Cashflow[],
  buckets: PlannedBucket[],
): CashflowBucket[] {
  const labelled = plannedSlots({ buckets, editableIndex: null })
  aggregateCashflowBuckets(
    cashflowData,
    labelled,
    labelled.at(-1)?.end_date ?? '',
  )
  return labelled
}

type AmountKey = 'total_amount' | 'coupon_amount' | 'principal_amount'

const SOURCE_AMOUNT_KEY: Record<CashflowSource, AmountKey> = {
  [CashflowSource.CouponAndPrincipal]: 'total_amount',
  [CashflowSource.Coupon]: 'coupon_amount',
  [CashflowSource.Principal]: 'principal_amount',
}

interface PlannedChart {
  data: CashflowBucket[]
  key: 'graded_amount'
}

interface PlannedChartOptions {
  // The day the backend grades on; targets ended by it drop out.
  dt: IsoDateStr
}

/** Slots for 2+ recurring targets that plan cleanly on `dt`, each graded on its own source; null otherwise. */
export function formatPlannedChart(
  cashflowData: Cashflow[],
  targets: ProposalCashflowTarget[],
  { dt }: PlannedChartOptions,
): PlannedChart | null {
  const recurring = targets.filter((t) => t.frequency !== 'one_time')
  if (recurring.length < 2) return null
  // Ended targets drop out, so a single live one still charts on its own windows.
  const plan = planCashflowBuckets(targets, { dt })
  if (plan.issues.length > 0 || plan.buckets.length === 0) return null
  const sources = [...new Set(plan.buckets.map((b) => b.source))]
  const [only] = sources
  // Gap months have no source; they show the shared source's cash, or the total when mixed.
  const gapKey =
    sources.length === 1 && only
      ? SOURCE_AMOUNT_KEY[PLANNED_SOURCE[only]]
      : 'total_amount'
  const data = formatPlannedData(cashflowData, plan.buckets).map((slot) => {
    const slotKey = slot.source ? SOURCE_AMOUNT_KEY[slot.source] : gapKey
    return { ...slot, graded_amount: slot[slotKey] }
  })
  return { data, key: 'graded_amount' }
}

// parseISO, not IsoDateStr: the accounts API sends datetime strings this path has always charted.
const isReadableDate = (value: string) => isValid(parseISO(value))

const hasReadableDates = (target: CashflowTarget) =>
  isReadableDate(target.end_date) &&
  (target.start_date == null || isReadableDate(target.start_date))

export function formatCustomData(
  cashflowData: Cashflow[],
  cashflowTargets: CashflowTarget[],
): CashflowBucket[] {
  // Stored dates are never checked at runtime, and an unreadable one throws in format().
  const readable = cashflowTargets.filter(hasReadableDates)
  const sortedTargets = readable.sort(
    (a, b) => parseISO(a.end_date).getTime() - parseISO(b.end_date).getTime(),
  )

  const buckets: CashflowBucket[] = sortedTargets.map((target, index) => {
    const derivedStartDate =
      target.start_date ??
      (index > 0
        ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
          sortedTargets[index - 1]!.end_date
        : format(new Date(), DateFormat.ISO_DATE))

    return {
      start_date: derivedStartDate,
      end_date: target.end_date,
      amount: target.amount ?? 0,
      start_date_formatted: format(
        parseISO(derivedStartDate),
        DateFormat.MONTH_ABBREV_YEAR_ABBREV,
      ),
      coupon_amount: 0,
      principal_amount: 0,
      dividend_amount: 0,
      total_amount: 0,
      source: target.source,
    }
  })

  // Custom targets never gap, so the sorted last target's end is always the true end.
  aggregateCashflowBuckets(
    cashflowData,
    buckets,
    sortedTargets.at(-1)?.end_date ?? '',
  )

  return buckets
}

export function getCashflowStatusMessage(
  actualAmount: number,
  targetAmount: number,
): string {
  const difference = Math.abs(actualAmount - targetAmount)

  if (difference <= 0.01) return 'At target'
  if (actualAmount > targetAmount)
    return `${formatMoney(difference, { numDigits: 0 })} over target`
  return `Need ${formatMoney(difference, { numDigits: 0 })} to meet target`
}

export function isCashflowTargetSuccessful(
  actualAmount: number,
  targetAmount: number,
): boolean {
  return actualAmount >= targetAmount
}

/**
 * Groups cashflow target rows by their source (filename)
 * Returns an object with source names as keys and arrays of rows as values
 */
export function groupCashflowRowsBySource(
  rows: CashflowTargetRowData[],
): Record<string, CashflowTargetRowData[]> {
  return rows.reduce(
    (groups, row) => {
      const key = row.sourceName || MANUAL_CASHFLOW_SOURCE
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(row)
      return groups
    },
    {} as Record<string, CashflowTargetRowData[]>,
  )
}
export function checkOverlaps(
  rows: CashflowTargetRowData[],
): CashflowTargetRowData[] {
  if (rows.length <= 1) {
    return rows.map((row) => ({ ...row, violation: false }))
  }

  const result = rows
    .map((row) => ({ ...row, violation: false }))
    .sort(
      (a, b) => parseISO(a.end_date).getTime() - parseISO(b.end_date).getTime(),
    )

  for (let i = 1; i < result.length; i++) {
    const prev = result[i - 1]
    const curr = result[i]

    // Both null start_dates don't overlap
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    if (!curr!.start_date && !prev!.start_date) continue

    // At this point, at least one has a start_date
    const hasOverlap =
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      !curr!.start_date || // curr unbounded, prev bounded = overlap
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      !prev!.start_date || // prev unbounded, curr bounded = overlap
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      parseISO(curr!.start_date).getTime() < parseISO(prev!.end_date).getTime()

    if (hasOverlap) {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      prev!.violation = true
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      curr!.violation = true
    }
  }

  return result
}

/**
 * Detects if cashflow targets contain one-time frequencies
 */
export function hasOneTimeCashflows(
  target: CashflowTarget[] | null | undefined,
): boolean {
  if (!target) return false
  return target.some((t) => t.frequency === CashflowFrequency.OneTime)
}

/**
 * Converts one-time cashflow targets to custom cashflow rows
 * This is the inverse of convertCashflowRowsToTargets
 * Preserves the original CashflowSource in the source field
 */
export function convertOneTimeTargetsToRows(
  target: CashflowTarget[] | null | undefined,
): CashflowTargetRowData[] {
  if (!target) return []

  return target
    .filter((t) => t.frequency === CashflowFrequency.OneTime)
    .map((t) => ({
      id: crypto.randomUUID(),
      amount: t.amount ?? 0,
      start_date: t.start_date,
      end_date: t.end_date,
      // The backend target doesn't persist a source name; saved one-time
      // targets are user-constructed, so they rejoin the manual group.
      sourceName: MANUAL_CASHFLOW_SOURCE,
      cashflowSource: t.source as CashflowSource,
    }))
}

/**
 * Converts an array of CashflowTargetRowData to an array of CashflowTarget objects
 * Each row becomes a one-time cashflow target for the backend
 * Returns null if there are any violations (overlapping date ranges)
 */
export function convertCashflowRowsToTargets(
  rows: CashflowTargetRowData[],
): CashflowTarget[] | null {
  const validRows = rows.filter(
    (row) =>
      (row.start_date === null || row.start_date) &&
      row.end_date &&
      row.amount > 0,
  )

  if (validRows.length === 0) {
    return []
  }

  if (validRows.some((row) => row.violation)) {
    return null
  }

  const result = validRows.map((row) => {
    return {
      amount: parseFloat(row.amount.toString()),
      start_date: row.start_date,
      end_date: row.end_date,
      frequency: CashflowFrequency.OneTime,
      source: row.cashflowSource || CashflowSource.CouponAndPrincipal,
      overrides: {},
      call_assumption: MaturityOption.maturity_date,
    }
  })

  return result
}
